import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { ZfBanner, ZfBox, ZfIcon, ZfLoadingSpinner, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import { ServiceAgreementV2, WaiverType } from 'pco-product-service-internal-consumer';
import { useGetCountryRiskWaivers } from 'hooks/queries/privateBanking';
import { OpenDocumentsInCrmButton } from 'views/PrivateBanking/OpenDocumentsInCrmButton';
import { CountryRiskWaiversGrid, WaiverRow } from './CountryRiskWaiversGrid';

const sectionStyle = {
  border: '1px solid var(--border-color-default-subtle, #e0e2e8)',
  padding: 'var(--padding-m, 24px)',
};

const descriptionStyle = { maxWidth: '50%' };

const bannerStyle = { containerType: 'inline-size' } as const;

const bannerDescriptionStyle = { display: 'block', width: 'calc(100cqw - 400px)', hyphens: 'manual' } as const;

const pageStyle = {
  width: '100%',
  minHeight: '100dvh',
  boxSizing: 'border-box',
  background: 'var(--bg-color-default-muted, #F8F9FB)',
  padding: 'var(--padding-vertical-xl, 24px)',
} as const;

const contentStyle = {
  background: 'white',
  border: '1px solid var(--border-color-default-normal, #E0E1EB)',
  boxSizing: 'border-box',
  padding: '24px',
} as const;

type SectionProps = {
  title: string;
  description: ReactNode;
  children: ReactNode;
};

const Section = ({ title, description, children }: SectionProps) => (
  <ZfStack direction="column" spacing="medium" style={sectionStyle}>
    <ZfStack direction="column" spacing="extra-small">
      <ZfTypography variant="title-medium" color="emphasis">
        {title}
      </ZfTypography>
      <ZfTypography variant="body-medium" color="muted" style={descriptionStyle}>
        {description}
      </ZfTypography>
    </ZfStack>
    {children}
  </ZfStack>
);

const toWaiverRow = (agreement: ServiceAgreementV2): WaiverRow => ({
  id: agreement.serviceAgreementId ?? '',
  waiver: agreement.serviceDefinitionName ?? '',
  status: agreement.agreementStatus,
});

export const CountryRiskWaivers = () => {
  const { t } = useTranslation();
  const { brUuid } = useParams();
  const { data, isLoading, isError } = useGetCountryRiskWaivers(brUuid);

  const rowsByWaiverType = useMemo(() => {
    const grouped: Record<WaiverType, WaiverRow[]> = {
      [WaiverType.GlobalWaivers]: [],
      [WaiverType.MarketSpecificWaivers]: [],
      [WaiverType.MarketsWithSubaccounts]: [],
    };
    (data?.list ?? []).forEach((agreement) => {
      if (agreement.waiverType) {
        grouped[agreement.waiverType].push(toWaiverRow(agreement));
      }
    });
    return grouped;
  }, [data]);

  if (isLoading) {
    return <ZfLoadingSpinner data-testid="country-risk-waivers-loading" />;
  }

  if (isError) {
    return (
      <ZfBanner
        type="error"
        dismissible={false}
        description={t('pb.countryriskwaivers.error')}
        data-testid="country-risk-waivers-error"
      />
    );
  }

  return (
    <ZfBox style={pageStyle} data-testid="country-risk-waivers">
      <ZfStack direction="column" spacing="medium" style={contentStyle}>
        <ZfStack direction="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <ZfTypography variant="title-large" role={'heading'}>
            {t('pb.countryriskwaivers.title')}
          </ZfTypography>
          <OpenDocumentsInCrmButton brUuid={brUuid} />
        </ZfStack>

        <ZfBanner type="info" dismissible={false} description={t('pb.countryriskwaivers.banner')} />

        <Section
          title={t('pb.countryriskwaivers.global.title')}
          description={t('pb.countryriskwaivers.global.description')}
        >
          <CountryRiskWaiversGrid rows={rowsByWaiverType[WaiverType.GlobalWaivers]} testId="global-waiver-grid" />
        </Section>

        <Section
          title={t('pb.countryriskwaivers.marketspecific.title')}
          description={t('pb.countryriskwaivers.marketspecific.description')}
        >
          <CountryRiskWaiversGrid
            rows={rowsByWaiverType[WaiverType.MarketSpecificWaivers]}
            testId="market-specific-waiver-grid"
          />
        </Section>

        <Section
          title={t('pb.countryriskwaivers.subaccounts.title')}
          description={t('pb.countryriskwaivers.subaccounts.description')}
        >
          <ZfBanner
            type="info"
            dismissible={false}
            titleText={t('pb.countryriskwaivers.subaccounts.frontusers.title')}
            style={bannerStyle}
          >
            <span style={bannerDescriptionStyle}>{t('pb.countryriskwaivers.subaccounts.frontusers.description')}</span>
            <ZfStack slot="trailing" direction="row" spacing="small" style={{ alignItems: 'center' }}>
              <ZfTextButton size="small" hierarchy="tertiary">
                <ZfIcon slot="trailing" name="mail" size="medium" />
                {t('pb.countryriskwaivers.subaccounts.contactgnm')}
              </ZfTextButton>
              <ZfTextButton size="small" hierarchy="secondary">
                {t('pb.countryriskwaivers.subaccounts.custodyregulations')}
              </ZfTextButton>
            </ZfStack>
          </ZfBanner>
          <CountryRiskWaiversGrid
            rows={rowsByWaiverType[WaiverType.MarketsWithSubaccounts]}
            testId="subaccount-waiver-grid"
          />
        </Section>
      </ZfStack>
    </ZfBox>
  );
};
